export function emptyProgress() { return { version: 2, xp: 0, levels: {}, lastClear: null }; }
/** Shared merge policy. Callers provide the storage transaction or retry. */
export function mergeProgress(a, b) {
    const levels = {};
    // Sorted, and not in argument order, because "the same record" has to mean
    // the same bytes. `canonical` serialises this object with JSON.stringify,
    // which preserves insertion order, and `sameProgress` compares those
    // strings — so built in argument order, merge(a, b) and merge(b, a) are
    // equal records that do not compare equal. That is not cosmetic: sameProgress
    // is what decides whether the account needs telling, so two devices holding
    // different levels would each read the other's record as new on every load
    // and push to one another for ever, a write apiece, for nothing.
    for (const id of [...new Set([...Object.keys(a.levels), ...Object.keys(b.levels)])].sort()) {
        const x = Object.hasOwn(a.levels, id) ? a.levels[id] : undefined;
        const y = Object.hasOwn(b.levels, id) ? b.levels[id] : undefined;
        levels[id] = x && y ? mergeLevel(x, y) : cloneLevel((x ?? y));
    }
    const xp = Object.values(levels).reduce((sum, lp) => sum + lp.xpEarned, 0);
    return { version: 2, xp, levels, lastClear: laterClock(a.lastClear, b.lastClear) };
}
/** A level from one side only. The flags are copied too, so the merged record shares no array with its inputs. */
function cloneLevel(lp) {
    return { ...lp, ...(lp.flags ? { flags: lp.flags.map((f) => ({ ...f })) } : {}) };
}
/**
 * The later of two clocks, and the same one whichever way round they are
 * given. Two clears inside one millisecond is not a real scenario, but an
 * answer that depends on argument order is the same perpetual-push bug as
 * the level ordering above, so the tie is broken on the fields rather than
 * on which argument won.
 */
function laterClock(x, y) {
    if (!x)
        return y;
    if (!y)
        return x;
    if (x.wall !== y.wall)
        return x.wall > y.wall ? x : y;
    if (x.mono !== y.mono)
        return x.mono > y.mono ? x : y;
    if (x.origin !== y.origin)
        return x.origin > y.origin ? x : y;
    return (x.missionId ?? '') <= (y.missionId ?? '') ? x : y;
}
function mergeLevel(x, y) {
    const completed = x.completed || y.completed;
    const verified = (x.completed && x.verified) || (y.completed && y.verified);
    // The hint count of the clear that earned the stars — and on a tie, the
    // lower of the two. A tie is reachable, not hypothetical: starsFor maps
    // both one hint and two to two stars, so the same star count can stand for
    // either. Preferring whichever side happened to be the first argument would
    // make the merge disagree with itself run the other way round.
    const hintsUsed = x.stars === y.stars ? Math.min(x.hintsUsed, y.hintsUsed) : (y.stars > x.stars ? y : x).hintsUsed;
    const merged = {
        completed,
        stars: Math.max(x.stars, y.stars),
        hintsUsed,
        attempts: Math.max(x.attempts, y.attempts),
        completedAt: x.completedAt === null ? y.completedAt : y.completedAt === null ? x.completedAt : Math.min(x.completedAt, y.completedAt),
        xpEarned: Math.max(x.xpEarned, y.xpEarned),
        verified,
    };
    if (completed && !verified) {
        const flags = [];
        for (const f of [...(x.flags ?? []), ...(y.flags ?? [])]) {
            const previous = flags.find((g) => g.code === f.code);
            if (!previous)
                flags.push({ ...f });
            else if (f.detail < previous.detail)
                previous.detail = f.detail;
        }
        // Sorted for the same reason the levels are: the order two sets of flags
        // happen to be concatenated in must not decide whether two merges of the
        // same record count as the same record.
        flags.sort((p, q) => (p.code < q.code ? -1 : p.code > q.code ? 1 : 0));
        if (flags.length)
            merged.flags = flags;
    }
    // Skipping is an or, like everything else here: a learner who moved past a
    // level on one device has moved past it on both. Assigned after `flags` so
    // that a merged record and a recorded one serialise their keys in the same
    // order — `sameProgress` compares the bytes, and two orderings of the same
    // record would push each other back and forth for ever.
    if (x.skipped || y.skipped)
        merged.skipped = true;
    return merged;
}
//# sourceMappingURL=progress.js.map